import React, {useEffect, useRef} from 'react';
import {FaChevronUp, FaChevronDown} from 'react-icons/fa';
import {useBottomSheet, BottomSheetState, BottomSheetContext} from '../hooks/useBottomSheet';

export interface BottomSheetProps {
    children: React.ReactNode;
    initialState?: BottomSheetState;
    persist?: boolean;
    className?: string;
    onStateChange?: (state: BottomSheetState) => void;
    /** When this value changes to a non-null value and the sheet is collapsed, auto-expands to half */
    expandTrigger?: unknown;
    /** Extra pixels added to the visible peek of the `half` state. Use this when
     *  content (e.g. a banner) is rendered above the events carousel so the
     *  carousel doesn't get pushed out of view. */
    extraHalfPx?: number;
}

/**
 * Bottom sheet component with drag handle and swipe gestures
 * Three snap states: collapsed (20%), half (50%), expanded (85%)
 * Supports touch and mouse events with velocity-based snapping
 */

export const BottomSheet: React.FC<BottomSheetProps> = ({
    children,
    initialState = 'collapsed',
    persist = false,
    className = '',
    onStateChange,
    expandTrigger,
    extraHalfPx = 0,
}) => {
    const {
        state,
        isDragging,
        translateY,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleTouchCancel,
        beginDragAt,
        cycleState,
        wasMoved,
        bottomSheetHeight,
        disallowExpanded,
        setDisallowExpanded,
    } = useBottomSheet({initialState, persist, extraHalfPx});

    const contentRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLDivElement>(null);
    // Tracks an in-progress pointer interaction on the sheet root before we've
    // decided whether it's a sheet drag or a content scroll. See the
    // engagement criteria in onSheetPointerMove.
    const pendingDragRef = useRef<{
        pointerId: number;
        startY: number;
        startedOnHandle: boolean;
        scrollContainer: HTMLElement | null;
        scrollAtStart: number;
        engaged: boolean;
    } | null>(null);

    // Auto-expand when a map marker is selected while the sheet is collapsed
    const stateRef = useRef(state);
    stateRef.current = state;
    useEffect(() => {
        if (expandTrigger != null && stateRef.current === 'collapsed') {
            cycleState('half');
        }
    }, [expandTrigger, cycleState]);

    // Notify parent of state changes
    useEffect(() => {
        if (onStateChange) {
            onStateChange(state);
        }
    }, [state, onStateChange]);

    // Prevent scroll on bottom sheet from affecting map
    useEffect(() => {
        const handleTouchMove = (e: TouchEvent) => {
            const target = e.target as HTMLElement;
            if (contentRef.current?.contains(target)) {
                e.stopPropagation();
            }
        };

        document.addEventListener('touchmove', handleTouchMove, {passive: false});
        return () => {
            document.removeEventListener('touchmove', handleTouchMove);
        };
    }, []);

    const ENGAGE_THRESHOLD_PX = 6;

    const findScrollableAncestor = (
        start: HTMLElement | null,
        stopAt: HTMLElement | null,
    ): HTMLElement | null => {
        let el: HTMLElement | null = start;
        while (el && el !== stopAt && el !== document.body) {
            const style = window.getComputedStyle(el);
            const overflowY = style.overflowY;
            if (
                (overflowY === 'auto' || overflowY === 'scroll') &&
                el.scrollHeight > el.clientHeight
            ) {
                return el;
            }
            el = el.parentElement;
        }
        return null;
    };

    const captureSafely = (target: HTMLElement, pointerId: number) => {
        try {
            target.setPointerCapture(pointerId);
        } catch {
            // Some browsers throw if the pointer was already captured / released.
        }
    };

    const onSheetPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        // Ignore re-entry from a still-active gesture (e.g. multi-touch).
        if (pendingDragRef.current) return;

        const target = e.target as HTMLElement;
        const startedOnHandle = handleRef.current?.contains(target) ?? false;
        const scrollContainer = startedOnHandle
            ? null
            : findScrollableAncestor(target, contentRef.current);

        pendingDragRef.current = {
            pointerId: e.pointerId,
            startY: e.clientY,
            startedOnHandle,
            scrollContainer,
            scrollAtStart: scrollContainer?.scrollTop ?? 0,
            engaged: false,
        };

        if (startedOnHandle) {
            // Handle gestures engage immediately so a pure tap still cycles.
            captureSafely(e.currentTarget, e.pointerId);
            handleTouchStart(e);
            pendingDragRef.current.engaged = true;
        }
    };

    const onSheetPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const pending = pendingDragRef.current;
        if (!pending || pending.pointerId !== e.pointerId) return;

        if (pending.engaged) {
            handleTouchMove(e);
            return;
        }

        const deltaY = e.clientY - pending.startY;
        if (Math.abs(deltaY) < ENGAGE_THRESHOLD_PX) return;

        // Engagement criteria for body drags (non-handle):
        //  - non-scrollable area inside the sheet → drag in either direction
        //  - scrollable area at scrollTop=0 + dragging down → drag (close sheet)
        //  - scrollable area otherwise → don't engage; native scroll runs
        const draggingDown = deltaY > 0;
        const noScrollable = pending.scrollContainer === null;
        const atScrollTop = pending.scrollAtStart === 0;
        const shouldEngage = noScrollable || (atScrollTop && draggingDown);

        if (!shouldEngage) {
            // Decision is final for this gesture — stop checking on each move.
            pendingDragRef.current = null;
            return;
        }

        // Engage retroactively: use the original pointerdown Y so the gesture
        // remains physically faithful (no visual snap when crossing threshold).
        beginDragAt(pending.startY);
        captureSafely(e.currentTarget, e.pointerId);
        pending.engaged = true;
        handleTouchMove(e);
    };

    const onSheetPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const pending = pendingDragRef.current;
        if (!pending || pending.pointerId !== e.pointerId) return;

        if (pending.engaged) {
            const moved = wasMoved();
            handleTouchEnd();
            // Cycle on a pure tap of the handle (no movement).
            if (!moved && pending.startedOnHandle) cycleState();
        }
        pendingDragRef.current = null;
    };

    const onSheetPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
        const pending = pendingDragRef.current;
        if (!pending || pending.pointerId !== e.pointerId) return;

        if (pending.engaged) handleTouchCancel();
        pendingDragRef.current = null;
    };

    const springTimingFunction = 'cubic-bezier(0.32, 0.72, 0, 1)';
    return (
        <BottomSheetContext.Provider value={{
            state,
            setState: (st: BottomSheetState) => cycleState(st),
            disallowExpanded,
            setDisallowExpanded,
        }}>
            <div
                /* `absolute` (not `fixed`) so the sheet's positioning context
                 * is the embed root (`.be-embedded-app`, which carries
                 * `position: relative + overflow: hidden`) instead of the
                 * host page's viewport. Without this, on a partner site the
                 * sheet would slide up over the host's header / nav and ignore
                 * the embed's bounds entirely.
                 */
                className={`bottom-sheet flex flex-col absolute bottom-0 left-0 right-0 bg-white/60 backdrop-blur-md rounded-t-[16px] shadow-[0_-4px_12px_rgba(0,0,0,0.15)] z-10 ${className}`}
                style={{
                    transform: `translateY(${translateY}px)`,
                    transition: isDragging ? 'none' : `transform 0.3s ${springTimingFunction}`,
                    willChange: isDragging ? 'transform' : 'auto',
                    height: `${bottomSheetHeight}px`,
                }}
                ref={contentRef}
                onPointerDown={onSheetPointerDown}
                onPointerMove={onSheetPointerMove}
                onPointerUp={onSheetPointerUp}
                onPointerCancel={onSheetPointerCancel}
            >
                {/* Drag Handle — visual + keyboard affordance. Pointer events are
                    handled on the sheet root so dragging works from anywhere on
                    the sheet (deferred-engage pattern preserves list scrolling). */}
                <div
                    style={{ touchAction: 'none' }}
                    ref={handleRef}
                    className="drag-handler w-full flex flex-col items-center gap-1 pt-3 pb-2 cursor-grab active:cursor-grabbing select-none"
                    role="button"
                    aria-label="Drag or tap to toggle events panel"
                    aria-expanded={state !== 'collapsed'}
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            cycleState();
                        }
                    }}
                >
                    <div
                        className="w-[48px] h-[5px] bg-balloon rounded-full"
                        aria-hidden="true"
                    />
                    <div
                        className="text-balloon text-[13px] leading-none"
                        aria-hidden="true"
                    >
                        {state === 'expanded' ? <FaChevronDown/> : <FaChevronUp/>}
                    </div>
                </div>

                {/* Content */}

                <div
                    className="bottom-list-content flex-1 w-full flex flex-col"
                    style={{height: `${bottomSheetHeight * 0.99 - 80}px`}}
                >
                    {children}
                </div>

            </div>
        </BottomSheetContext.Provider>
    );
};
